import { useCallback, useEffect, useState } from 'react'
import { CheckCircle2, XCircle } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { CollectedAt, Pill, ReliabilityChip, label } from './components/primitives'
import {
  createSource, listCollectors, listSources, testSource,
  type Collector, type Source, type SourceTest,
} from './api'

/**
 * Publications, their drivers and their Admiralty reliability.
 *
 * ## Adding a newspaper is a row, not a release
 *
 * A collector is a DRIVER — `news.rss`, `api.json`. A source is a configured publication.
 * The Business Daily and the Wall Street Journal are two rows on the same driver, each with
 * its own feed URL and — the part that matters — **its own reliability grade**. Reliability
 * is a property of a publication, so two publications cannot share a source row.
 *
 * The form below is generated from the selected driver's `configKeys`, so a new driver gets a
 * working form without a frontend release.
 *
 * ## Test before you save
 *
 * `POST /sources/{id}/test` fetches once and writes nothing. A wrong URL, a moved feed or a
 * robots policy that forbids us surfaces while somebody is looking at the form, rather than
 * as an empty run three hours later that nobody connects to the change they made.
 */
export function SourcesPage() {
  const [sources, setSources] = useState<Source[] | null>(null)
  const [collectors, setCollectors] = useState<Collector[]>([])
  const [error, setError] = useState<string | null>(null)
  const [tests, setTests] = useState<Record<string, SourceTest>>({})
  const [adding, setAdding] = useState(false)

  const load = useCallback(async () => {
    try {
      setError(null)
      const [s, c] = await Promise.all([listSources(), listCollectors()])
      setSources(s)
      setCollectors(c)
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function runTest(id: string) {
    try {
      const result = await testSource(id)
      setTests((t) => ({ ...t, [id]: result }))
    } catch (err) {
      setError(explain(err))
    }
  }

  if (error && !sources) return <ErrorStrip message={error} />
  if (!sources) return <LoadingBlock label="Loading sources" />

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Sources & grading"
        description="Each publication carries its own Admiralty reliability. Adding one is a
                     row here — no driver needs writing for a feed or a JSON API."
      />

      {error && <ErrorStrip message={error} />}

      <div className="flex gap-2">
        <button type="button" onClick={() => setAdding((a) => !a)}
                className="rounded border border-border px-2 py-1 text-[12px]">
          {adding ? 'Close' : 'Add a publication'}
        </button>
      </div>

      {adding && <AddSourceForm collectors={collectors} onDone={() => { setAdding(false); void load() }} />}

      <Panel>
        <ErpTable head={<>
          <Th>Source</Th>
          <Th>Kind</Th>
          <Th>Driver</Th>
          <Th>Reliability</Th>
          <Th className="text-right axis-hide-sm">Items graded</Th>
          <Th className="axis-hide-sm">Last fetch</Th>
          <Th>Test</Th>
        </>}>
          {sources.length === 0 && (
            <EmptyRow colSpan={7}
                      message="No sources yet. Every item is attributed to one, so add a
                               publication before collecting." />
          )}
          {sources.map((s) => {
            const test = tests[s.id]
            return (
              <tr key={s.id} className="hover:bg-muted/40">
                <Td>
                  <span className="font-medium">{s.name}</span>
                  <span className="block font-mono text-[10px] text-muted-foreground">
                    {s.code}
                  </span>
                </Td>
                <Td><Pill className="border-border">{label(s.kindCode)}</Pill></Td>
                <Td>
                  {s.driverCode
                    ? <span className="font-mono text-[11px]">{s.driverCode}</span>
                    : <span className="text-[11px] text-muted-foreground">hand-written</span>}
                </Td>
                <Td>
                  {/* The letter ALONE. A source has no credibility digit — that belongs to a
                      claim — and printing a filler one would be a number nobody chose. */}
                  <ReliabilityChip reliability={s.reliability} note={s.reliabilityNote} />
                </Td>
                <Td className="text-right tabular-nums axis-hide-sm">{s.itemCount}</Td>
                <Td className="axis-hide-sm"><CollectedAt at={s.lastFetchAt} /></Td>
                <Td>
                  {s.driverCode ? (
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => void runTest(s.id)}
                              className="rounded border border-border px-1.5 py-0.5 text-[11px]">
                        Test
                      </button>
                      {test && (
                        <span className="inline-flex items-center gap-1 text-[11px]"
                              title={test.message ?? undefined}>
                          {test.reachable
                            ? <CheckCircle2 className="size-3.5 text-emerald-600" />
                            : <XCircle className="size-3.5 text-rose-600" />}
                          {test.itemsFound} found
                          {test.robotsDecision && (
                            <span className="text-muted-foreground">
                              · robots {test.robotsDecision.toLowerCase()}
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">nothing to fetch</span>
                  )}
                </Td>
              </tr>
            )
          })}
        </ErpTable>
      </Panel>

      {Object.entries(tests).some(([, t]) => t.sampleTitles.length > 0) && (
        <Panel>
          <div className="space-y-2 p-2">
            <p className="text-[11px] font-medium">What the last test found</p>
            {Object.entries(tests).map(([id, t]) => t.sampleTitles.length > 0 && (
              <ul key={id} className="space-y-0.5">
                {t.sampleTitles.map((title) => (
                  <li key={title} className="text-[11px] text-muted-foreground">· {title}</li>
                ))}
              </ul>
            ))}
          </div>
        </Panel>
      )}
    </div>
  )
}

/** The form is GENERATED from the driver's declared config keys — never hand-written. */
function AddSourceForm({ collectors, onDone }: {
  collectors: Collector[]; onDone: () => void
}) {
  // '' means NO DRIVER — a hand-written source (MANUAL/FIELD) that is written to through
  // manual entry and never fetched from. Without this option the form cannot create the
  // desk source the manual-entry door requires, which makes that whole door unreachable.
  const [driverCode, setDriverCode] = useState('')
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [kindCode, setKindCode] = useState('MANUAL')
  const [reliability, setReliability] = useState('C')
  const [reliabilityNote, setReliabilityNote] = useState('')
  const [config, setConfig] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const driver = collectors.find((c) => c.code === driverCode)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await createSource({
        code, name, kindCode: kindCode || driver?.supportedSourceKindCodes[0] || 'MANUAL',
        reliability, reliabilityNote,
        driverCode: driverCode || null,
        fetchConfig: config,
      })
      onDone()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel>
      <form onSubmit={submit} className="grid gap-2 p-3 sm:grid-cols-2">
        {error && <div className="sm:col-span-2"><ErrorStrip message={error} /></div>}

        <Field label="Driver">
          <select value={driverCode}
                  onChange={(e) => {
                    setDriverCode(e.target.value)
                    setConfig({})
                    const next = collectors.find((c) => c.code === e.target.value)
                    setKindCode(next?.supportedSourceKindCodes[0] ?? 'MANUAL')
                  }}
                  className="h-7 w-full rounded border border-input bg-background px-2 text-[12px]">
            <option value="">None — written to by hand</option>
            {collectors.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </Field>
        <Field label="Source kind">
          <select value={kindCode} onChange={(e) => setKindCode(e.target.value)}
                  className="h-7 w-full rounded border border-input bg-background px-2 text-[12px]">
            {(driver?.supportedSourceKindCodes ?? ['MANUAL', 'FIELD']).map((k) => (
              <option key={k} value={k}>{label(k)}</option>
            ))}
          </select>
        </Field>
        <Field label="Code">
          <input value={code} onChange={(e) => setCode(e.target.value)} required
                 placeholder="business-daily"
                 className="h-7 w-full rounded border border-input bg-background px-2
                            font-mono text-[12px]" />
        </Field>
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} required
                 placeholder="Business Daily"
                 className="h-7 w-full rounded border border-input bg-background px-2 text-[12px]" />
        </Field>
        <Field label="Admiralty reliability">
          <select value={reliability} onChange={(e) => setReliability(e.target.value)}
                  className="h-7 w-full rounded border border-input bg-background px-2 text-[12px]">
            {['A', 'B', 'C', 'D', 'E', 'F'].map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </Field>
        <Field label="Why that grade">
          <input value={reliabilityNote} onChange={(e) => setReliabilityNote(e.target.value)}
                 placeholder="Established daily; corrections policy published"
                 className="h-7 w-full rounded border border-input bg-background px-2 text-[12px]" />
        </Field>

        {driver?.requiresCredentials && (
          <p className="sm:col-span-2 text-[11px] text-amber-600">
            This driver needs credentials, so the source must point at an integration. Public
            sources — an RSS feed, an open register — need none.
          </p>
        )}

        {(driver?.configKeys ?? []).map((key) => (
          <Field key={key.key} label={`${key.label}${key.required ? ' *' : ''}`}>
            <input
              value={config[key.key] ?? ''}
              required={key.required}
              placeholder={key.example ?? ''}
              onChange={(e) => setConfig((c) => ({ ...c, [key.key]: e.target.value }))}
              className="h-7 w-full rounded border border-input bg-background px-2 text-[12px]"
            />
          </Field>
        ))}

        <div className="sm:col-span-2">
          <button type="submit" disabled={busy}
                  className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                             text-primary-foreground disabled:opacity-50">
            {busy ? 'Adding…' : 'Add publication'}
          </button>
        </div>
      </form>
    </Panel>
  )
}

function Field({ label: text, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{text}</span>
      {children}
    </label>
  )
}
